import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface Studio {
  id: string;
  name: string;
  subdomain: string;
  timezone: string;
  currency: string;
}

interface StudioState {
  // Current studio context
  currentStudio: Studio | null;
  studios: Studio[];
  
  // Actions
  setCurrentStudio: (studio: Studio) => void;
  clearCurrentStudio: () => void;
  addStudio: (studio: Studio) => void;
  updateStudio: (id: string, updates: Partial<Studio>) => void;
  removeStudio: (id: string) => void;
  setStudios: (studios: Studio[]) => void;
}

export const useStudioStore = create<StudioState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        currentStudio: null,
        studios: [],

        // Actions
        setCurrentStudio: (studio) =>
          set(
            (state) => ({ currentStudio: studio }),
            false,
            'setCurrentStudio'
          ),

        clearCurrentStudio: () =>
          set(
            { currentStudio: null },
            false,
            'clearCurrentStudio'
          ),

        addStudio: (studio) =>
          set(
            (state) => ({
              studios: [...state.studios, studio],
            }),
            false,
            'addStudio'
          ),

        updateStudio: (id, updates) =>
          set(
            (state) => ({
              studios: state.studios.map((studio) =>
                studio.id === id ? { ...studio, ...updates } : studio
              ),
              currentStudio:
                state.currentStudio?.id === id
                  ? { ...state.currentStudio, ...updates }
                  : state.currentStudio,
            }),
            false,
            'updateStudio'
          ),

        removeStudio: (id) =>
          set(
            (state) => ({
              studios: state.studios.filter((studio) => studio.id !== id),
              currentStudio:
                state.currentStudio?.id === id ? null : state.currentStudio,
            }),
            false,
            'removeStudio'
          ),

        setStudios: (studios) =>
          set(
            { studios },
            false,
            'setStudios'
          ),
      }),
      {
        name: 'studio-storage', // unique name for localStorage
        partialize: (state) => ({
          currentStudio: state.currentStudio,
          studios: state.studios,
        }),
      }
    ),
    {
      name: 'StudioStore',
    }
  )
);